/**
 * User hooks — TanStack Query wrappers for the users API.
 *
 * Hooks:
 * - useUsers(filters?)      — paginated list of users with optional filters
 * - useCreateUser()         — mutation to create a new user
 * - useUpdateUser()         — mutation to update a user's fields (PATCH)
 * - useDeactivateUser()     — mutation to deactivate a user
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPatch } from '@/lib/api'

// ── Row type ────────────────────────────────────────────────────────────────

export interface UserRow {
  id: string
  email: string
  name: string
  role: string
  active: boolean
  oauth_provider: string | null
  team_id: string | null
  territory_id: string | null
  email_verified_at: string | null
  created_at: string
  updated_at: string
  deleted_at: string | null
}

// ── Filter types ────────────────────────────────────────────────────────────

export interface UserListFilters {
  role?: string
  active?: boolean
  search?: string
  page?: number
  limit?: number
}

// ── Response types ──────────────────────────────────────────────────────────

interface UsersListApiResponse {
  users: UserRow[]
}

interface UserDetailApiResponse {
  user: UserRow
}

// ── Query key factory ───────────────────────────────────────────────────────

const userKeys = {
  all: ['users'] as const,
  list: (filters?: UserListFilters) => [...userKeys.all, 'list', filters] as const,
}

// ── Hooks ───────────────────────────────────────────────────────────────────

export function useUsers(filters?: UserListFilters) {
  const params = new URLSearchParams()
  if (filters?.role) params.set('role', filters.role)
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  if (filters?.search) params.set('search', filters.search)
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters?.page !== undefined) {
    const limit = filters.limit ?? 25
    const offset = (filters.page - 1) * limit
    params.set('offset', String(offset))
  }
  const qs = params.toString()
  const path = `/api/v1/users${qs ? `?${qs}` : ''}`

  return useQuery<UserRow[]>({
    queryKey: userKeys.list(filters),
    queryFn: async () => {
      const res = await apiGet<UsersListApiResponse>(path)
      return res.users
    },
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation<UserRow, Error, { email: string; name: string; role: string }>({
    mutationFn: async (data) => {
      const res = await apiPost<UserDetailApiResponse>('/api/v1/users', data)
      return res.user
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation<UserRow, Error, { id: string; data: Record<string, unknown> }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPatch<UserDetailApiResponse>(`/api/v1/users/${id}`, data)
      return res.user
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

export function useDeactivateUser() {
  const queryClient = useQueryClient()

  return useMutation<UserRow, Error, string>({
    mutationFn: async (userId) => {
      const res = await apiPost<UserDetailApiResponse>(`/api/v1/users/${userId}/deactivate`, {})
      return res.user
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}
